import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

export default function QuizReporting() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const { userId } = useParams();
  const csvLinkRef = useRef();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateFilterApplied, setDateFilterApplied] = useState(false);

  const customStyles = {
    headCells: {
      style: {
        fontWeight: 'bold', // Bold header text
        fontSize: '16px', // Increase font size
        backgroundColor: '#f4f4f4', // Light gray background
      },
    },
  };

  // Memoize the CSV preparation function
  const prepareCSVData = useCallback(
    topicsData => {
      const data = [];

      // Add Main Header
      data.push({
        'Quiz Topics': '',
        Views: '',
        'Topic View Percentage': '',
        '': '',
        '': '',
      });
      data.push({
        'Quiz Topics': '',
        Views: '',
        'Topic View Percentage': '',
        '': '',
        '': '',
      });

      topicsData.forEach(topic => {
        // Add Topic Header
        data.push({
          'Quiz Topics': topic.topicName,
          Views: `Views: ${topic.views}`,
          'Topic View Percentage': `${topic.percentage}%`,
          '': '',
          '': '',
        });

        // Add Empty Row
        data.push({
          'Quiz Topics': '',
          Views: '',
          'Topic View Percentage': '',
          '': '',
          '': '',
        });
      });

      setCsvData(data);
    },
    [dateFilterApplied, startDate, endDate]
  );

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate.toISOString().split('T')[0];
      if (endDate) params.endDate = endDate.toISOString().split('T')[0];

      const response = await axios.get(`/api/get-topic-count/${userId}`, {
        params,
        timeout: 10000,
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      setTopics(response.data);
      prepareCSVData(response.data);
    } catch (error) {
      console.log('error', error);
      // showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [userId, prepareCSVData, startDate, endDate]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  const fetchAnswers = async topicId => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate.toISOString().split('T')[0];
      if (endDate) params.endDate = endDate.toISOString().split('T')[0];
      const response = await axios.get(`/api/get-topic-answer-count/${userId}/${topicId}`, {
        params,
        timeout: 10000,
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching answers:', error);
      return [];
    }
  };

  // Memoize the CSV update function
  const updateCSVData = useCallback((topicName, questions) => {
    setCsvData(prevData => {
      const newData = [...prevData.filter(item => !item.topicName || item.topicName !== topicName)];

      // Add Topic Header
      newData.push({
        'Quiz Topics': '',
        Views: '',
        'Topic View Percentage': '',
        '': '',
        '': '',
        'Topic Name': topicName,
      });

      // Add Topic Subheaders
      newData.push({
        Question: 'Question Text',
        Options: 'All Options',
        'Selected Count': 'Response Count',
        'Correct Answer': 'Correct Option',
        'Answer Percentage': 'Total Percentage',
      });

      questions.forEach((q, qIndex) => {
        // Add Question Row
        newData.push({
          Question: `Q${qIndex + 1}: ${q.question.replace(/"/g, '""')}`,
          Options: '',
          'Selected Count': '',
          'Correct Answer': '',
          'Answer Percentage': '',
        });

        // Add Options Rows
        q.options.forEach(option => {
          newData.push({
            Question: '',
            Options: option.option,
            'Selected Count': option.count,
            'Correct Answer': option.option === q.correctAnswer ? 'Correct' : '',
            'Answer Percentage': `${option.percentage}%`,
          });
        });

        // Add Empty Row for Spacing
        newData.push({
          Question: '',
          Options: '',
          'Selected Count': '',
          'Correct Answer': '',
          'Answer Percentage': '',
        });
      });

      // Add Section Divider
      newData.push({
        'Quiz Topics': '',
        Views: '',
        'Topic View Percentage': '',
        '': '',
        '': '',
        'Topic Name': '',
      });
      newData.push({
        'Quiz Topics': '',
        Views: '',
        'Topic View Percentage': '',
        '': '',
        '': '',
        'Topic Name': '',
      });

      return newData;
    });
  }, []);

  const handleExpand = async (expanded, row) => {
    if (expanded) {
      const answers = await fetchAnswers(row.topicId);
      updateCSVData(row.topicName, answers);
    }
  };

  const ExpandedComponent = ({ data }) => {
    const [questions, setQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(true);

    useEffect(() => {
      async function fetchAndSetQuestions() {
        const fetchedQuestions = await fetchAnswers(data.topicId);
        setQuestions(fetchedQuestions);
        setLoadingQuestions(false);
      }
      fetchAndSetQuestions();
    }, [data.topicId]);

    const expandcolumns = [
      {
        name: 'Answer',
        selector: row => row.option,
        sortable: true,
      },
      {
        name: 'Count',
        selector: row => row.count,
        sortable: true,
      },
      {
        name: 'Percentage',
        selector: row => (row.percentage ? `${row.percentage}%` : ''),
        sortable: true,
      },
    ];
    const customStyles2 = {
      headCells: {
        style: {
          fontWeight: 'bold', // Bold header text
          fontSize: '16px', // Increase font size
          backgroundColor: '#f4f4f4', // Light gray background
        },
      },
    };

    return (
      <div style={{ padding: '54px' }}>
        {loadingQuestions ? (
          <div>Loading questions...</div>
        ) : questions.length > 0 ? (
          (questions || []).map((q, qIndex) => (
            <div key={qIndex} style={{ marginBottom: '30px' }}>
              <div style={{ marginBottom: '10px' }}>
                <h4>{`Q:${qIndex + 1} ${q.question}`}</h4>
              </div>
              <div style={{ marginBottom: '20px', marginTop: '10px' }}>
                <h6>{`Correct Answer: ${q.correctAnswer}`}</h6>
              </div>
              <DataTable
                customStyles={customStyles2}
                columns={expandcolumns}
                data={q.options}
                noHeader
                striped
                highlightOnHover
                pagination={false}
                style={{ marginTop: '10px' }}
              />
            </div>
          ))
        ) : (
          <div>No questions found.</div>
        )}
      </div>
    );
  };

  const columns = [
    {
      name: 'Topic Name',
      selector: row => row.topicName,
      sortable: true,
    },
    {
      name: 'Views',
      selector: row => row.views,
      sortable: true,
    },

    {
      name: 'Percentage',
      selector: row => `${row.percentage}%`,
      sortable: true,
    },
  ];

  const handleDateFilterReset = () => {
    setStartDate(null);
    setEndDate(null);
    setDateFilterApplied(false);
    fetchTopics();
  };

  const dateFilterControls = (
    <div className="row mb-3 p-3">
      <div className="col-md-3">
        <label>Start Date</label>
        <DatePicker
          selected={startDate}
          onChange={date => setStartDate(date)}
          selectsStart
          startDate={startDate}
          endDate={endDate}
          className="form-control"
          placeholderText="Select start date"
        />
      </div>
      <div className="col-md-3">
        <label>End Date</label>
        <DatePicker
          selected={endDate}
          onChange={date => setEndDate(date)}
          selectsEnd
          startDate={startDate}
          endDate={endDate}
          minDate={startDate}
          className="form-control"
          placeholderText="Select end date"
        />
      </div>
      <div className="col-md-3 d-flex align-items-end">
        {/* <button 
                    onClick={handleDateFilterApply}
                    className="btn btn-primary mr-2"
                    disabled={!startDate && !endDate}
                >
                    Apply Filter
                </button> */}
        <button
          onClick={handleDateFilterReset}
          className="btn btn-secondary"
          // disabled={!dateFilterApplied}
        >
          Reset
        </button>
      </div>
    </div>
  );

  return (
    <div className="main-container">
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className="page-header">
            <div className="row">
              <div className="col-md-6 col-sm-12">
                <div className="title">
                  <h4>Quiz Interaction</h4>
                </div>
              </div>
              <div className="col-md-6 col-sm-12 text-right">
                <CSVLink
                  data={csvData}
                  filename={`quiz_report_${new Date().toISOString().slice(0, 10)}.csv`}
                  className="btn btn-primary"
                  ref={csvLinkRef}
                >
                  Export to CSV
                </CSVLink>
              </div>
            </div>
          </div>
          <div className="card-box mb-30">
            {dateFilterControls}
            <DataTable
              noHeader
              customStyles={customStyles}
              columns={columns}
              data={topics}
              progressPending={loading}
              expandableRows
              expandableRowsComponent={ExpandedComponent}
              onRowExpandToggled={handleExpand}
              pagination
            />
          </div>
        </div>
      </div>
    </div>
  );
}
